import type { FilterValue, SortOrder } from "antd/es/table/interface";

import { TableParams } from "../../../../types";

/**
 * URL persistence for the Mandanten activation view (SMT-1247, AC2).
 *
 * Only `email` used to be kept in the URL, so a "Load all" list plus a
 * Client-state filter reset to an empty view on a browser reload. These helpers
 * map the view's table state to/from query params so the same list, filter,
 * sort and page can be seeded again after F5.
 *
 * The Client-state filter is keyed by the joined dataIndex `user.kontaxStatus`
 * (see `UsersTable`), which is also the key `handleTableChange` stores it under.
 */
export const CLIENT_STATE_KEY = "user.kontaxStatus";

/**
 * antd filter key for the Assignee (Bearbeiter) column (its joined dataIndex).
 * The assignee filter is applied via the separate `assigneeId` param, not the
 * backend `filters` payload, so this key is stripped from that payload in
 * `UserList` — but the column still needs a `filteredValue` so the table is not
 * "partially controlled" once the Client-state column has one (SMT-1247).
 */
export const ASSIGNEE_FILTER_KEY = "user.kontaxUser";
export const FIRM_FILTER_KEY = "user.firm";

export interface ViewQueryState {
  clientState?: (string | null)[] | null;
  sortField?: string | null;
  sortOrder?: string | null;
  page?: number | null;
}

const isSortOrder = (value?: string | null): value is SortOrder =>
  value === "ascend" || value === "descend";

/**
 * Build the initial `TableParams` from the URL query params.
 *
 * The values come straight from the URL, which a person can hand-edit, so they
 * are validated before they reach `getUsers`: `page` is clamped to a positive
 * integer, and sort is only applied with a real `ascend`/`descend` order.
 */
export const seedTableParams = ({
  clientState,
  sortField,
  sortOrder,
  page,
}: ViewQueryState): TableParams => {
  const selected = clientState?.filter((value): value is string =>
    Boolean(value)
  );
  const current =
    typeof page === "number" && Number.isInteger(page) && page > 0 ? page : 1;

  return {
    pagination: { current, pageSize: 10 },
    ...(selected?.length
      ? { filters: { [CLIENT_STATE_KEY]: selected as FilterValue } }
      : {}),
    ...(sortField && isSortOrder(sortOrder)
      ? { field: sortField, order: sortOrder }
      : {}),
  };
};

/** The selected Client-state values, or undefined when the filter is cleared. */
export const clientStateFromTableParams = (
  tableParams: TableParams
): string[] | undefined => {
  const selected = tableParams.filters?.[CLIENT_STATE_KEY] as
    | (string | number)[]
    | null
    | undefined;

  return selected && selected.length ? selected.map(String) : undefined;
};

/** The active sort field as a single string (nested dataIndex joined by `.`). */
export const sortFieldFromTableParams = (
  tableParams: TableParams
): string | undefined => {
  const field = Array.isArray(tableParams.field)
    ? tableParams.field.join(".")
    : (tableParams.field as string | undefined);

  return field || undefined;
};
